import { useEffect, useRef } from "react";
import { useAtom, useSetAtom } from "jotai";
import { useNavigate, useLocation } from "react-router-dom";
import posthog from "posthog-js";
import * as Sentry from "@sentry/react";

import {
  parseExpertResult,
  type Noticeboard,
  type NoticeboardResult,
} from "shared/lib";
import {
  accountVar,
  applicationPageVar,
  isAuthenticatedVar,
  languageVar,
  noticeboardDataVar,
  Pages,
} from "@/variables/globalVar";

import { useGetAccount, useGetAuthTest } from "shared/apiHooks";
import { useChannel } from "shared/channel";
import { useCouncilLocalisation } from "shared/hooks";
import { useAccount, useInitGoogleMaps } from "@/hooks";

import { Toaster } from "shared/components";
import { CustomTitle } from "@/components/CustomTitle";

import { MyLotPage } from "./pages/MyLotPage";
import { AddressPage } from "./pages/AddressPage";
import { MultiLingualToastContainer } from "@/components/multiLingualToast";

const isDemo = import.meta.env.VITE_REACT_ENV === "demo";
const API_BASE_URL = import.meta.env.VITE_REACT_APP_BACKEND_ENDPOINT;

export function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const globalDebugRef = useRef<any>({});
  const { channel } = useChannel();

  const [applicationPage, setApplicationPageVar] = useAtom(applicationPageVar);
  const [isAuthenticated, setIsAuthenticatedVar] = useAtom(isAuthenticatedVar);
  const setAccountVar = useSetAtom(accountVar);
  const [language, setLanguageVar] = useAtom(languageVar);
  const [noticeboardData, setNoticeboardDataVar] = useAtom(noticeboardDataVar);

  const { account, application, conversation } = useAccount();
  const { getLocalisationEntry } = useCouncilLocalisation(conversation);

  useEffect(() => {
    (window as any).mylot = globalDebugRef.current;
  }, []);

  useEffect(() => {
    globalDebugRef.current.account = account;
  }, [account]);

  useEffect(() => {
    globalDebugRef.current.application = application;
  }, [application]);

  useEffect(() => {
    globalDebugRef.current.conversation = conversation;
  }, [conversation]);

  useEffect(() => {
    globalDebugRef.current.noticeboardData = noticeboardData;
  }, [noticeboardData]);

  useInitGoogleMaps();

  const { isError, isSuccess } = useGetAuthTest({
    enabled: isAuthenticated == true,
    retry: false,
  });

  useEffect(() => {
    if (isSuccess) {
      setIsAuthenticatedVar(true);
    } else if (isError) {
      setIsAuthenticatedVar(false);
    }
  }, [isError, isSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: accountResponse } = useGetAccount({
    enabled: isAuthenticated,
    retry: false,
  });

  useEffect(() => {
    if (isAuthenticated && accountResponse?.data?.email) {
      const email = accountResponse.data.email;
      posthog.identify(accountResponse.data.email, {
        email,
        uuid: accountResponse.data?.id,
      });
      Sentry.setUser({ email });
    }
  }, [accountResponse, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && channel && channel.isClosed) {
      channel.initSocket();
    }
  }, [channel, isAuthenticated]);

  useEffect(() => {
    console.log("<App> accountResponse.data", accountResponse?.data);

    if (accountResponse?.data.id) {
      const account = accountResponse.data;
      setAccountVar(accountResponse?.data);

      const currentApplication = account.current_application;
      const currentConversation = currentApplication?.current_conversation;

      if (currentConversation) {
        setLanguageVar(currentConversation.language);
        Sentry.setTag("conversationId", currentConversation.id);
        Sentry.setTag("identifier", currentConversation.identifier);
        Sentry.setTag("language", currentConversation.language);
      }

      const noticeboard = currentConversation?.noticeboard;
      if (noticeboard) {
        const noticeboardArray =
          noticeboard as unknown as Array<NoticeboardResult>;

        setNoticeboardDataVar({});
        setNoticeboardDataVar((draft) => {
          noticeboardArray.forEach((noticeboardResult) => {
            draft[noticeboardResult.expert_name] =
              parseExpertResult(noticeboardResult);
          }, {} as Noticeboard);
        });
      }
    }
  }, [accountResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // There is no current conversation or associated address, render the address selection wizard
    if (!conversation?.address_data) {
      setApplicationPageVar(Pages.Address);
    }
  }, [conversation]); // eslint-disable-line react-hooks/exhaustive-deps

  const councilName = isDemo ? "myLot" : getLocalisationEntry("councilName");

  return (
    <>
      <CustomTitle councilName={councilName} />

      {isAuthenticated && applicationPage === Pages.Address && <AddressPage />}
      {isAuthenticated && applicationPage === Pages.App && <MyLotPage />}

      <MultiLingualToastContainer />

      <Toaster />
    </>
  );
}

export default App;
